import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
} from "react-query";
import { TimeRange } from "../../pages/Clusters/types";
import { useToken } from "../../utils/hooks";
import { getClusterLogs, getClusterInfraEvents } from "./fetch";
import { useEffect, useState } from "react";
import { getLatestTimestamp } from "../../pages/Clusters/Information/utils";
import { LogEntry, LogPage } from "./types";
import { REFRESH_LOGS_INTERVAL } from "../../pages/Clusters/Information/const";
import { ApiClient } from "../../apiUtils";
import { useInterval } from "usehooks-ts";
import { LogSessionPage } from "../../api-client";

const LOG_INCREMENT_MS = 1000 * 60 * 10; // 10 minutes

export const previousTimeInterval = (
  internalTimeRange: TimeRange,
): TimeRange => {
  return [internalTimeRange[0] - LOG_INCREMENT_MS, internalTimeRange[0] - 1];
};

export const useAzureLogs = (
  clusterId: number,
  dask: boolean,
  system: boolean,
  liveUpdates?: boolean,
): UseInfiniteQueryResult<LogSessionPage, Error> => {
  const query = useInfiniteQuery({
    queryKey: ["azureLogs", clusterId, dask, system],
    queryFn: (context) => {
      return ApiClient.declarativeViewsLogsNextClusterLogsPost({
        pk: clusterId,
        logPageSessionRequestSchema: {
          encodedSession: context.pageParam,
          dask,
          system,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.tail;
    },
    getPreviousPageParam: (firstPage) => firstPage.back,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
  });

  useEffect(() => {
    // no deps, so this effect is called on each re-render which is what we want
    // because every time we load a previous page there's a re-render and we want to
    // then fetch another previous page if there is one
    if (query.hasPreviousPage && !query.isFetchingPreviousPage) {
      query.fetchPreviousPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.hasPreviousPage,
    query.isFetchingPreviousPage,
    query.fetchPreviousPage,
  ]);

  useInterval(() => {
    if (liveUpdates && query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, 10000);

  return query as UseInfiniteQueryResult<LogSessionPage, Error>;
};

export const useClusterLogs = (
  clusterId: string,
  dask: boolean,
  system: boolean,
  timeRange?: TimeRange,
  instanceIds?: number[],
  liveUpdates?: boolean,
): UseInfiniteQueryResult<LogPage, Error> => {
  const [latestTimer, setLatestTimer] = useState<number>();
  const token = useToken();

  const query = useInfiniteQuery({
    queryKey: ["clusterLogs", token, clusterId, dask, system, instanceIds],
    queryFn: async ({ signal, pageParam }) => {
      // If nextTimeRange is undefined it means we are fetching the first page,
      // let's start fetching backwards from the end of the current time range
      const startTimeRange = timeRange
        ? previousTimeInterval([timeRange![1], timeRange![1]] as TimeRange)
        : undefined;
      const requestTimeRange =
        pageParam?.nextTimeRange ||
        pageParam?.previousTimeRange ||
        startTimeRange ||
        undefined;

      const logsPromise = getClusterLogs(
        token,
        clusterId,
        dask,
        system,
        requestTimeRange[0],
        requestTimeRange[1],
        undefined,
        instanceIds,
        signal,
      );

      // When the cluster is running, we'll trigger a refetch by setting a timer
      // Setting latestTimestamp will trigger a refetch after both the previous
      // query has finished and REFRESH_LOGS_INTERVAL has passed
      if (liveUpdates) {
        (async () => {
          if (!pageParam?.previousTimeRange) {
            let timer;
            const intervalPromise = new Promise((res) => {
              timer = setTimeout(res, REFRESH_LOGS_INTERVAL);
            });
            await Promise.allSettled([logsPromise, intervalPromise]);
            setLatestTimer(timer);
          }
        })();
      }

      const logs = await logsPromise;

      // Reverse the logs so that the most recent ones are at the top
      return {
        timeRange: requestTimeRange,
        data: logs.reverse(),
      };
    },
    getNextPageParam: (_, pages) => {
      const ts = getLatestTimestamp(pages);
      return {
        nextTimeRange: [ts ? ts + 1 : timeRange![0], Date.now()],
      };
    },
    enabled: !!timeRange,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
  });

  // This effect is responsible for fetching live log data
  useEffect(() => {
    if (latestTimer) {
      query.fetchNextPage();
    }

    return () => {
      clearTimeout(latestTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestTimer, query.fetchNextPage]); // Disabled exhaustive-deps, query causes cycles

  return query as UseInfiniteQueryResult<LogPage, Error>;
};

export const useClusterInfraEvents = (
  clusterId: string,
  defaultTimeRange?: TimeRange,
): UseQueryResult<LogEntry[]> => {
  const token = useToken();

  return useQuery({
    queryKey: ["clusterInfraEvents", token, clusterId, defaultTimeRange],
    queryFn: async ({ signal }) =>
      getClusterInfraEvents(token, clusterId, signal),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
  });
};
